import React from 'react';
import { Clock, CheckCircle, MessageSquare, HardHat, ShieldCheck } from "lucide-react";

const rows = [
  {
    number: "01",
    icon: Clock,
    title: "Planned Around Your Program",
    description: "We coordinate our plumbing scope around your construction program, keeping the work aligned with site access, sequencing and project requirements.",
  },
  {
    number: "02",
    icon: CheckCircle,
    title: "Work Delivered to Requirements",
    description: "Our plumbing work is carried out with reference to applicable Australian Standards, project specifications and the requirements of the site.",
  },
  {
    number: "03",
    icon: MessageSquare,
    title: "Clear Communication Throughout",
    description: "We keep site teams and project managers informed as the work progresses, with clear communication around scope, access, sequencing and any issues that arise.",
  },
  {
    number: "04",
    icon: HardHat,
    title: "Built Around Commercial Sites",
    description: "We work with builders, project managers and site teams on commercial plumbing scopes, with an approach suited to active construction environments.",
  },
  {
    number: "05",
    icon: ShieldCheck,
    title: "Safety &amp; Site Responsibility",
    description: "We follow site safety procedures and applicable WHS requirements, with safety considered throughout the planning and delivery of each job.",
  },
];

export default function CommitmentSection() {
  return (
    <section className="relative overflow-hidden bg-[#080808] py-16 md:py-24">
      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 md:px-14 lg:px-20">
        {/* Heading */}
        <div className="mb-10 md:mb-16">
          <p className="text-[#60a5fa] font-semibold tracking-[0.2em] uppercase text-xs mb-4">Our Commitment</p>
          <h2 className="font-bold text-white leading-[1.1] tracking-tight" style={{ fontSize: 'clamp(1.6rem, 5vw, 3rem)' }}>
            What You Can<br />Expect From Oliver &amp; Sons.
          </h2>
        </div>

        {/* Rows */}
        <div className="border-t border-white/5">
          {rows.map((row) => (
            <div
              key={row.number}
              className="group relative flex items-center gap-4 md:gap-8 py-6 md:py-10 border-b border-white/5 pl-4 md:pl-6 transition-all duration-300 hover:bg-[#0f1a2e]/50 cursor-default"
            >
              {/* Blue left border */}
              <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#2563eb]/40 group-hover:bg-[#2563eb] transition-all duration-300 rounded-full" />

              {/* Large number */}
              <span
                className="text-[40px] md:text-[96px] font-bold leading-none select-none flex-shrink-0 w-14 md:w-36 text-center transition-all duration-300"
                style={{ color: '#3a6fa8', opacity: 0.5 }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                onMouseLeave={e => e.currentTarget.style.opacity = '0.5'}
              >
                {row.number}
              </span>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-bold text-base md:text-2xl mb-1 md:mb-2 leading-snug">{row.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed max-w-2xl">{row.description}</p>
              </div>

              {/* Icon */}
              <div className="hidden md:flex flex-shrink-0 w-11 h-11 rounded-full bg-[#1e3a5f]/30 border border-[#1e3a5f]/40 items-center justify-center group-hover:border-[#2563eb]/50 group-hover:bg-[#1e3a5f]/60 transition-all duration-300">
                <row.icon className="w-5 h-5 text-[#5a9cf8]" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}